/**
 * WeeklyCraDialog — Iter-80 / Iter-81
 *
 * Modal triggered from the timesheet to compose, preview and send a weekly CRA
 * (Compte Rendu d'Activité hebdomadaire) for one (mission × ISO week).
 *
 * Iter-81 additions:
 *  - Daily totals come from the timesheet (static bottom row, read-only).
 *  - User can't allocate more hours than the day's timesheet total — blocked + toast.
 *  - Auto-save (debounced 600ms) so user edits survive a page reload / modal close.
 *  - Once a week is sent, the dialog opens in read-only mode (lock UI + Send hidden).
 *  - Pressing Enter in a description input adds a new task and focuses it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Eye, Plus, X, Repeat, Trash2, FileText, Lock, Save, GripVertical } from "lucide-react";
import { useLongPress } from "@/lib/useLongPress";
import { useAuth } from "@/lib/auth";
import { getEmailHistory, rememberEmail } from "@/lib/emailHistory";
// Iter-108: drag-and-drop task reordering
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven"];

// Iter-108: stable unique id per task (for dnd-kit). We NEVER send this to the backend
// — it's client-only and regenerated fresh on each dialog open.
function _uid() {
  return `t_${Math.random().toString(36).slice(2, 10)}`;
}

function formatHours(v) {
  const n = Number(v) || 0;
  if (!n) return "0h";
  return n === Math.floor(n) ? `${n}h` : `${n.toFixed(1)}h`;
}

function HourCell({ value, onIncrement, onClear, disabled }) {
  // Iter-117: unified mobile gestures — tap = +2h, double-tap = +8h, long-press = clear.
  // Desktop keeps the same semantics (click / dblclick / right-click).
  const handlers = useLongPress({
    onTap: () => !disabled && onIncrement(2),
    onDoubleTap: () => !disabled && onIncrement(8),
    onLongPress: () => !disabled && value > 0 && onClear(),
  });
  return (
    <button
      type="button"
      data-testid="wkcra-hour-cell"
      {...handlers}
      disabled={disabled}
      className={`w-full h-9 border rounded text-xs font-mono transition-colors select-none ${
        disabled ? "bg-zinc-50 border-zinc-100 text-zinc-300 cursor-not-allowed"
          : value > 0
            ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
            : "bg-white border-zinc-200 text-zinc-400 hover:bg-zinc-50"
      }`}
      style={{ touchAction: "manipulation" }}
      title={value > 0 ? `${formatHours(value)} — tap +2h · double-tap +8h · long-press / right-click clear` : "Tap +2h · Double-tap +8h · Right-click +8h"}
    >
      {value > 0 ? formatHours(value) : "–"}
    </button>
  );
}

// Iter-108: one row of the weekly CRA task table, made draggable via dnd-kit.
// The `<tr>` receives the sortable transform so users can reorder rows vertically
// by grabbing the handle in the leftmost cell. Extracted as a stable named component
// so hook rules are respected even when the tasks array length changes.
function SortableTaskRow({
  uid, idx, task, isLocked,
  updateTask, incTaskDay, clearTaskDay, toggleRecurring, removeTask,
  onDescKeyDown, taskInputsRef,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: uid, disabled: isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    // Keeps the row above siblings while dragging so the visual follows the pointer
    position: isDragging ? "relative" : undefined,
    zIndex: isDragging ? 10 : undefined,
    boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.12)" : undefined,
    background: isDragging ? "white" : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-zinc-100"
      data-testid={`wkcra-task-row-${idx}`}
    >
      <td className="px-1 py-2 align-top w-8">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={isLocked}
          data-testid={`wkcra-task-drag-${idx}`}
          title={isLocked ? "Verrouillé" : "Glisser pour réordonner"}
          aria-label="Réordonner cette tâche"
          className={`p-1 rounded text-zinc-300 hover:text-zinc-700 hover:bg-zinc-100 cursor-grab active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-30 touch-none`}
        >
          <GripVertical size={14} />
        </button>
      </td>
      <td className="px-3 py-2 align-top">
        <Input
          data-testid={`wkcra-task-desc-${idx}`}
          value={task.description}
          onChange={(e) => updateTask(idx, { description: e.target.value })}
          onKeyDown={(e) => onDescKeyDown(e, idx)}
          ref={(el) => { if (el) taskInputsRef.current[idx] = el; }}
          placeholder="Animation cérémonies, coaching, ..."
          className="text-sm"
          disabled={isLocked}
        />
        {task.is_recurring && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-violet-700">
            <Repeat size={10} /> Récurrente
          </span>
        )}
      </td>
      {DAY_LABELS.map((_lbl, dayIdx) => (
        <td key={dayIdx} className="px-1 py-2 align-top w-20">
          <HourCell
            value={Number(task.hours_by_day[dayIdx]) || 0}
            onIncrement={(by) => incTaskDay(idx, dayIdx, by)}
            onClear={() => clearTaskDay(idx, dayIdx)}
            disabled={isLocked}
          />
        </td>
      ))}
      <td className="px-2 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => toggleRecurring(idx)}
            data-testid={`wkcra-task-recurring-${idx}`}
            disabled={isLocked}
            title={task.is_recurring ? "Ne plus reprendre cette tâche" : "Enregistrer comme récurrente"}
            className={`p-1.5 rounded hover:bg-zinc-100 disabled:opacity-30 ${task.is_recurring ? "text-violet-700" : "text-zinc-400"}`}
          >
            <Repeat size={13} />
          </button>
          <button
            type="button"
            onClick={() => removeTask(idx)}
            data-testid={`wkcra-task-remove-${idx}`}
            disabled={isLocked}
            className="p-1.5 rounded hover:bg-red-50 text-zinc-400 hover:text-red-600 disabled:opacity-30"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function WeeklyCraDialog({ open, onOpenChange, missionId: initialMissionId, year, isoWeek, missionTitle: initialMissionTitle, onSent, impersonatedUserId, impersonatedUserName, availableMissions = [] }) {
  const { user } = useAuth();
  // Iter-193 — internal active-mission state so the dialog can switch between the
  // user's assigned active missions without re-opening. Reset every time the parent
  // mounts the dialog with a different mission.
  const [activeMissionId, setActiveMissionId] = useState(initialMissionId);
  const [activeMissionTitle, setActiveMissionTitle] = useState(initialMissionTitle);
  useEffect(() => {
    setActiveMissionId(initialMissionId);
    setActiveMissionTitle(initialMissionTitle);
  }, [initialMissionId, initialMissionTitle]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null); // server response from /preview
  const [tasks, setTasks] = useState([]); // [{description, hours_by_day:{0..4}, is_recurring}]
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [ccDraft, setCcDraft] = useState("");
  const [recurring, setRecurring] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Iter-131: CEO/ADMIN impersonation — when set, the dialog operates on behalf of
  // another user. Read-only by default until the explicit "Edit" button is clicked,
  // and Send asks for a mandatory submission reason.
  const isImpersonating = !!impersonatedUserId;
  const [editOnBehalf, setEditOnBehalf] = useState(false);
  const [submissionReason, setSubmissionReason] = useState("");
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const impersonationQS = isImpersonating ? `?user_id=${encodeURIComponent(impersonatedUserId)}` : "";
  // Iter-104: per-user email history (To + Cc), surfaced via <datalist>
  const toHistory = user ? getEmailHistory(user.user_id, "wkcra-to") : [];
  const ccHistory = user ? getEmailHistory(user.user_id, "wkcra-cc") : [];
  const taskInputsRef = useRef({}); // idx → DOM node
  const focusTaskIdxRef = useRef(null); // schedule a focus on next render
  const dirtyRef = useRef(false); // becomes true on first user edit (gate auto-save)

  const isLockedRaw = !!preview?.is_locked;
  // Iter-131: CEO/ADMIN viewing on behalf is read-only by default; the user must
  // click the explicit "Edit on behalf" button to unlock the editor.
  const isLocked = isLockedRaw || (isImpersonating && !editOnBehalf);
  const isFutureWeek = !!preview?.is_future_week;
  const isFullLeaveWeek = !!preview?.is_full_leave_week;
  // Daily totals from the timesheet (Mon..Fri) — STATIC, can't be changed.
  const dailyTotals = useMemo(() => {
    const out = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    const raw = preview?.daily_totals || {};
    [0, 1, 2, 3, 4].forEach((i) => {
      const v = raw[i] ?? raw[String(i)] ?? 0;
      out[i] = Number(v) || 0;
    });
    return out;
  }, [preview]);
  const grandTotalStatic = useMemo(() => Object.values(dailyTotals).reduce((a, v) => a + v, 0), [dailyTotals]);

  // Iter-86: per-day reconciliation — tasks hours must equal timesheet hours for every
  // weekday. Returns {ok, perDayTaskSum, mismatches:[{dayIdx,label,taskSum,timesheet}]}.
  const perDayCheck = useMemo(() => {
    const DAY_LBL = { 0: "Lundi", 1: "Mardi", 2: "Mercredi", 3: "Jeudi", 4: "Vendredi" };
    const perDayTaskSum = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    tasks.forEach((t) => {
      [0, 1, 2, 3, 4].forEach((i) => {
        const v = Number(t.hours_by_day?.[i] ?? t.hours_by_day?.[String(i)] ?? 0);
        perDayTaskSum[i] += isFinite(v) ? v : 0;
      });
    });
    const mismatches = [];
    if (!isFullLeaveWeek) {
      [0, 1, 2, 3, 4].forEach((i) => {
        const diff = perDayTaskSum[i] - (dailyTotals[i] || 0);
        if (Math.abs(diff) > 1e-6) {
          mismatches.push({
            dayIdx: i, label: DAY_LBL[i],
            taskSum: perDayTaskSum[i], timesheet: dailyTotals[i] || 0, diff,
          });
        }
      });
    }
    return { ok: mismatches.length === 0, perDayTaskSum, mismatches };
  }, [tasks, dailyTotals, isFullLeaveWeek]);

  // Iter-86: CC domain whitelist — only @ba-inmotion.com or the client's email domain.
  const clientDomain = useMemo(() => {
    const m = (toEmail || "").toLowerCase().split("@");
    return m.length === 2 ? m[1].trim() : "";
  }, [toEmail]);
  const isDomainAllowed = (addr) => {
    const d = (addr || "").toLowerCase().split("@");
    if (d.length !== 2) return false;
    const dom = d[1].trim();
    return dom === "ba-inmotion.com" || (!!clientDomain && dom === clientDomain);
  };

  // Iter-83: full-leave weeks bypass the future-week guard (the user can pre-announce
  // the leave to the client). The Send button stays active.
  // Iter-86: also disable Send when per-day task hours don't reconcile with timesheet.
  // (Declared AFTER perDayCheck so the const can be safely read — fixes TDZ ReferenceError.)
  const sendDisabled = loading || !preview || sending || (isFutureWeek && !isFullLeaveWeek) || !perDayCheck.ok;

  const reset = () => {
    setPreview(null); setTasks([]); setSubject(""); setBodyText("");
    setToEmail(""); setCcEmails([]); setCcDraft(""); setRecurring([]);
    setDraftSavedAt(null); setSavingDraft(false);
    taskInputsRef.current = {};
    focusTaskIdxRef.current = null;
    dirtyRef.current = false;
  };

  useEffect(() => {
    if (!open || !activeMissionId || !year || !isoWeek) return;
    setLoading(true); reset();
    (async () => {
      try {
        const r = await api.get(`/weekly-cra/${activeMissionId}/${year}/${isoWeek}/preview${impersonationQS}`);
        setPreview(r.data);
        const normTasks = (r.data.tasks || []).map((t) => {
          const h = {};
          const raw = t.hours_by_day || {};
          [0, 1, 2, 3, 4].forEach((i) => {
            const v = raw[i] ?? raw[String(i)] ?? 0;
            h[i] = Number(v) || 0;
          });
          return { _uid: _uid(), description: t.description || "", hours_by_day: h, is_recurring: !!t.is_recurring };
        });
        if (normTasks.length === 0) {
          normTasks.push({ _uid: _uid(), description: "", hours_by_day: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }, is_recurring: false });
        }
        setTasks(normTasks);
        setSubject(r.data.subject || "");
        setBodyText(r.data.body_text || "");
        setToEmail(r.data.to_email || "");
        setCcEmails(r.data.cc_emails || []);
        setRecurring(r.data.recurring_tasks || []);
        setDraftSavedAt(r.data.draft_saved_at || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Unable to load weekly CRA");
      } finally { setLoading(false); }
    })();
  }, [open, activeMissionId, year, isoWeek]);

  // Iter-81: per-task per-day total (user-allocated). Used for the validation pass.
  const perDaySum = useMemo(() => {
    const out = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    tasks.forEach((t) => {
      [0, 1, 2, 3, 4].forEach((i) => {
        out[i] += Number(t.hours_by_day?.[i]) || 0;
      });
    });
    return out;
  }, [tasks]);

  // Iter-81: auto-save draft (debounced 600ms). Skipped while locked.
  useEffect(() => {
    if (!preview || isLocked || !dirtyRef.current) return;
    const handle = setTimeout(async () => {
      setSavingDraft(true);
      try {
        const payload = {
          tasks: tasks.map((t) => ({ description: t.description, hours_by_day: t.hours_by_day })),
          subject, body_text: bodyText,
        };
        const r = await api.put(`/weekly-cra/${activeMissionId}/${year}/${isoWeek}/draft${impersonationQS}`, payload);
        setDraftSavedAt(r.data?.updated_at || new Date().toISOString());
      } catch (e) {
        // Surface 409 (week locked) — but other errors stay silent (network blip).
        if (e?.response?.status === 409) {
          toast.error(e.response.data?.detail || "Weekly CRA verrouillé");
        }
      } finally { setSavingDraft(false); }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, subject, bodyText, isLocked, preview]);

  // Auto-focus a freshly-added task's description input.
  useEffect(() => {
    const idx = focusTaskIdxRef.current;
    if (idx !== null && taskInputsRef.current[idx]) {
      try { taskInputsRef.current[idx].focus(); } catch (_e) { /* noop */ }
      focusTaskIdxRef.current = null;
    }
  }, [tasks]);

  const markDirty = () => { dirtyRef.current = true; };

  const updateTask = (idx, patch) => {
    if (isLocked) return;
    markDirty();
    setTasks((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const incTaskDay = (idx, dayIdx, by) => {
    if (isLocked) return;
    markDirty();
    setTasks((prev) => prev.map((t, i) => {
      if (i !== idx) return t;
      const h = { ...t.hours_by_day };
      const current = Number(h[dayIdx]) || 0;
      // Cap by daily timesheet total — the user can't allocate more than what the
      // timesheet records for that day across all tasks (sum of perDaySum).
      const cap = Number(dailyTotals[dayIdx]) || 0;
      const otherTasks = (perDaySum[dayIdx] || 0) - current;
      const room = Math.max(0, cap - otherTasks);
      const desired = current + by;
      if (desired > room + 1e-9) {
        const dayLbl = ["lundi", "mardi", "mercredi", "jeudi", "vendredi"][dayIdx];
        if (cap === 0) {
          toast.error(`Le timesheet ne contient aucune heure le ${dayLbl} sur cette mission. Imputez d'abord côté timesheet.`);
        } else {
          toast.error(
            `Le total ${dayLbl} ne peut pas dépasser ${formatHours(cap)} ` +
            `(timesheet). Ajustez votre CRA hebdomadaire ou votre timesheet.`
          );
        }
        // Snap to the cap if there's any room left, otherwise leave unchanged.
        h[dayIdx] = Math.min(desired, room) > current ? room : current;
      } else {
        h[dayIdx] = Math.min(8, desired);
      }
      return { ...t, hours_by_day: h };
    }));
  };
  const clearTaskDay = (idx, dayIdx) => {
    if (isLocked) return;
    markDirty();
    setTasks((prev) => prev.map((t, i) => {
      if (i !== idx) return t;
      const h = { ...t.hours_by_day };
      h[dayIdx] = 0;
      return { ...t, hours_by_day: h };
    }));
  };
  const addTask = useCallback((desc = "") => {
    if (isLocked) return;
    markDirty();
    setTasks((prev) => {
      const next = [...prev, { _uid: _uid(), description: desc, hours_by_day: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 }, is_recurring: false }];
      focusTaskIdxRef.current = next.length - 1;
      return next;
    });
  }, [isLocked]);
  const removeTask = (idx) => {
    if (isLocked) return;
    markDirty();
    setTasks((prev) => prev.filter((_, i) => i !== idx));
  };

  // Iter-108: dnd-kit sensors + handler. Keyboard sensor enables Tab-into-handle +
  // Space/Arrow reordering for accessibility. Require a tiny pointer movement so
  // click-into-description input isn't mistaken for a drag start.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || isLocked) return;
    setTasks((prev) => {
      const oldIndex = prev.findIndex((t) => t._uid === active.id);
      const newIndex = prev.findIndex((t) => t._uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      markDirty();
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const onDescKeyDown = (e, idx) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Iter-81: Enter on the description field appends a new task and focuses it.
      addTask("");
    }
  };

  const toggleRecurring = async (idx) => {
    if (isLocked) return;
    const t = tasks[idx];
    if (!t.description.trim()) {
      toast.error("Renseignez la description avant de marquer la tâche comme récurrente");
      return;
    }
    if (t.is_recurring) {
      const match = recurring.find((r) => (r.description || "").trim().toLowerCase() === t.description.trim().toLowerCase());
      if (match) {
        try {
          await api.delete(`/weekly-cra/recurring-tasks/${match.task_id}`);
          setRecurring((prev) => prev.filter((x) => x.task_id !== match.task_id));
          toast.success("Tâche récurrente supprimée");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not remove recurring task");
          return;
        }
      }
      updateTask(idx, { is_recurring: false });
    } else {
      try {
        const r = await api.post(`/weekly-cra/${activeMissionId}/recurring-tasks`, { description: t.description.trim() });
        setRecurring((prev) => [...prev, r.data]);
        updateTask(idx, { is_recurring: true });
        toast.success("Tâche enregistrée comme récurrente");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not save recurring task");
      }
    }
  };

  const addCcEmail = () => {
    if (isLocked) return;
    const e = ccDraft.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Email invalide"); return; }
    if (ccEmails.includes(e)) { toast.error("Déjà en Cc"); return; }
    // Iter-86: only @ba-inmotion.com or the client's email domain are accepted in Cc.
    if (!isDomainAllowed(e)) {
      toast.error(`Domaine refusé : seul @ba-inmotion.com ou @${clientDomain || "(domaine du client)"} sont autorisés en Cc`);
      return;
    }
    setCcEmails([...ccEmails, e]); setCcDraft("");
  };
  const removeCcEmail = (e) => { if (isLocked) return; setCcEmails((prev) => prev.filter((x) => x !== e)); };

  const buildPayload = () => ({
    tasks: tasks
      .filter((t) => t.description.trim() && Object.values(t.hours_by_day).some((v) => Number(v) > 0))
      .map((t) => ({ description: t.description.trim(), hours_by_day: t.hours_by_day })),
    subject, body_text: bodyText, to_email: toEmail, cc_emails: ccEmails,
  });

  const onPreviewPdf = async () => {
    const payload = buildPayload();
    if (payload.tasks.length === 0) { toast.error("Renseignez au moins une tâche avec des heures"); return; }
    try {
      const r = await api.post(
        `/weekly-cra/${activeMissionId}/${year}/${isoWeek}/pdf${impersonationQS}`,
        // Iter-90: forward the To field so the PDF "Validateur" cell mirrors the
        // email currently entered in the dialog (the actual client recipient).
        { tasks: payload.tasks, to_email: payload.to_email },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview PDF");
    }
  };

  const onSend = async () => {
    if (isLocked && !(isImpersonating && editOnBehalf)) return;
    const payload = buildPayload();
    if (payload.tasks.length === 0) { toast.error("Renseignez au moins une tâche avec des heures"); return; }
    if (!payload.to_email) { toast.error("L'email destinataire est requis"); return; }
    // Iter-86: pre-flight reconciliation check — blocks before hitting the backend.
    if (!perDayCheck.ok) {
      const m = perDayCheck.mismatches[0];
      toast.error(
        `${m.label} : ${m.taskSum}h sur les tâches ≠ ${m.timesheet}h timesheet. ` +
        "Toutes les journées doivent être entièrement imputées sur les tâches avant l'envoi."
      );
      return;
    }
    // Iter-131: when CEO/ADMIN submits on behalf, ask for a mandatory reason first.
    if (isImpersonating) {
      setReasonDialogOpen(true);
      return;
    }
    await doSend(payload, "");
  };

  const doSend = async (payload, reason) => {
    setSending(true);
    try {
      const finalPayload = { ...payload, ...(reason ? { submission_reason: reason } : {}) };
      await api.post(`/weekly-cra/${activeMissionId}/${year}/${isoWeek}/send${impersonationQS}`, finalPayload);
      toast.success(isImpersonating
        ? `CRA hebdo envoyé au nom de ${impersonatedUserName || "l'utilisateur"} · Semaine ${isoWeek}`
        : `Weekly CRA envoyé · Semaine ${isoWeek}`);
      // Iter-104: remember the freshly-used emails so the user finds them in the
      // datalist suggestions next time without retyping.
      if (user) {
        if (toEmail) rememberEmail(user.user_id, "wkcra-to", toEmail);
        if (ccEmails.length) rememberEmail(user.user_id, "wkcra-cc", ...ccEmails);
      }
      setReasonDialogOpen(false);
      setSubmissionReason("");
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send weekly CRA");
    } finally { setSending(false); }
  };

  const onSubmitReason = async () => {
    const reason = (submissionReason || "").trim();
    if (!reason) { toast.error("Le motif est obligatoire"); return; }
    await doSend(buildPayload(), reason);
  };

  const totalBaseEqual = useMemo(() => {
    // Sum of allocated hours equals the timesheet total — eligible for a "balanced" tag.
    const allocated = Object.values(perDaySum).reduce((a, v) => a + v, 0);
    return Math.abs(allocated - grandTotalStatic) < 1e-6;
  }, [perDaySum, grandTotalStatic]);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="weekly-cra-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isLocked ? <Lock size={16} className="text-emerald-700" /> : <FileText size={16} />}
            Weekly CRA — Semaine {isoWeek} · {activeMissionTitle}
            {isLocked && (
              <span data-testid="wkcra-locked-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800">
                <Lock size={10} /> Lecture seule · Envoyé
              </span>
            )}
            {!isLocked && isFutureWeek && (
              <span data-testid="wkcra-future-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800">
                Semaine future · Envoi désactivé
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {preview ? `Période : ${preview.period_short} · ${preview.period_long}` : "Loading…"}
            {isLocked && preview?.sent_at && (
              <span className="ml-2 text-emerald-700">· Envoyé le {new Date(preview.sent_at).toLocaleDateString()}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Iter-131: CEO/ADMIN impersonation banner + Edit toggle ----------- */}
        {isImpersonating && (
          <div data-testid="wkcra-impersonation-banner" className={`-mt-2 mb-2 rounded-md border px-4 py-2.5 flex items-center gap-3 ${editOnBehalf ? "bg-orange-50 border-orange-300" : "bg-blue-50 border-blue-300"}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-zinc-900">
                {editOnBehalf ? "Édition au nom de" : "Lecture seule · CRA hebdo de"} {impersonatedUserName || "l'utilisateur"}
              </div>
              <div className="text-[11px] text-zinc-600 mt-0.5">
                {editOnBehalf
                  ? "Vous pouvez modifier les tâches et soumettre. Un motif sera demandé à l'envoi."
                  : "Cliquez sur Éditer pour modifier les tâches au nom de cet utilisateur."}
              </div>
            </div>
            {!isLockedRaw && (
              editOnBehalf ? (
                <button
                  data-testid="wkcra-cancel-edit-on-behalf"
                  onClick={() => setEditOnBehalf(false)}
                  className="px-3 h-8 text-xs border border-zinc-300 bg-white text-zinc-700 rounded-md hover:bg-zinc-50"
                >
                  Annuler l'édition
                </button>
              ) : (
                <button
                  data-testid="wkcra-edit-on-behalf-btn"
                  onClick={() => setEditOnBehalf(true)}
                  className="px-3 h-8 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Éditer
                </button>
              )
            )}
          </div>
        )}

        {/* Iter-193 — mission switcher: lets the user flip between assigned ACTIVE
            missions without closing the dialog. Hidden when only one mission. */}
        {availableMissions.length > 1 && (
          <div
            data-testid="wkcra-mission-switcher"
            className="-mt-1 mb-3 flex items-center gap-2 flex-wrap border-b border-zinc-100 pb-3"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mr-1">
              Mission
            </div>
            {availableMissions.map((m) => {
              const isActive = m.mission_id === activeMissionId;
              return (
                <button
                  key={m.mission_id}
                  type="button"
                  data-testid={`wkcra-switch-mission-${m.mission_id}`}
                  onClick={() => {
                    if (m.mission_id === activeMissionId) return;
                    if (sending || savingDraft) return;
                    setActiveMissionId(m.mission_id);
                    setActiveMissionTitle(m.title || "Mission");
                  }}
                  className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-zinc-950 text-white"
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  }`}
                  title={m.title}
                >
                  <span className="truncate inline-block max-w-[220px] align-middle">
                    {m.title}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Chargement…</div>
        ) : !preview ? null : (
          <div className="space-y-6">
            {/* Tasks table */}
            <div className="space-y-2" data-testid="wkcra-tasks-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Tâches · Heures par jour</Label>
                <div className="flex items-center gap-2 text-xs">
                  {!isLocked && (
                    <span data-testid="wkcra-draft-status" className="text-zinc-400 inline-flex items-center gap-1">
                      {savingDraft ? (
                        <><Save size={11} className="animate-pulse" /> Enregistrement…</>
                      ) : draftSavedAt ? (
                        <><Save size={11} /> Brouillon enregistré · {new Date(draftSavedAt).toLocaleTimeString()}</>
                      ) : null}
                    </span>
                  )}
                  <span data-testid="wkcra-grand-total" className={`font-mono font-bold px-2 py-1 rounded ${totalBaseEqual ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    Total alloué : {formatHours(Object.values(perDaySum).reduce((a, v) => a + v, 0))} / {formatHours(grandTotalStatic)} (timesheet)
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto -mx-1 px-1">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <table className="w-full text-sm border-collapse min-w-[600px]" data-testid="wkcra-task-table">
                    <thead>
                      <tr className="bg-zinc-950 text-white">
                        <th className="px-2 py-2 w-8" />
                        <th className="text-left px-3 py-2 font-medium text-xs">Description de la tâche</th>
                        {DAY_LABELS.map((lbl, i) => (
                          <th key={lbl} className="px-2 py-2 text-center font-medium text-xs">
                            {lbl}
                            <div className="font-mono text-[10px] text-zinc-400">
                              {preview.days[i] ? new Date(preview.days[i]).getDate() : ""}
                            </div>
                          </th>
                        ))}
                        <th className="px-2 py-2 w-20" />
                      </tr>
                    </thead>
                    <tbody>
                      <SortableContext items={tasks.map((t) => t._uid)} strategy={verticalListSortingStrategy}>
                        {tasks.map((t, idx) => (
                          <SortableTaskRow
                            key={t._uid}
                            uid={t._uid}
                            idx={idx}
                            task={t}
                            isLocked={isLocked}
                            updateTask={updateTask}
                            incTaskDay={incTaskDay}
                            clearTaskDay={clearTaskDay}
                            toggleRecurring={toggleRecurring}
                            removeTask={removeTask}
                            onDescKeyDown={onDescKeyDown}
                            taskInputsRef={taskInputsRef}
                          />
                        ))}
                      </SortableContext>
                      {/* Iter-81: STATIC bottom row — daily totals from the timesheet */}
                      <tr className="bg-zinc-50 border-t-2 border-zinc-300" data-testid="wkcra-totals-row">
                        <td />
                        <td className="px-3 py-2 align-middle text-xs font-bold uppercase tracking-wider text-zinc-700">
                          Total jour (timesheet)
                        </td>
                        {DAY_LABELS.map((_lbl, dayIdx) => {
                          const allocated = Number(perDaySum[dayIdx]) || 0;
                          const cap = Number(dailyTotals[dayIdx]) || 0;
                          const tone = cap === 0
                            ? "text-zinc-400"
                            : allocated > cap + 1e-6
                              ? "bg-red-100 text-red-800"
                              : Math.abs(allocated - cap) < 1e-6
                                ? "bg-emerald-100 text-emerald-800"
                                : "text-zinc-700";
                          return (
                            <td key={dayIdx} className="px-1 py-2 text-center" data-testid={`wkcra-day-total-${dayIdx}`}>
                              <div className={`mx-auto inline-flex items-center justify-center min-w-[52px] h-9 rounded font-mono text-xs font-semibold ${tone}`}>
                                {formatHours(cap)}
                              </div>
                            </td>
                          );
                        })}
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </DndContext>
              </div>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <button
                  type="button"
                  onClick={() => addTask("")}
                  data-testid="wkcra-add-task"
                  disabled={isLocked}
                  className="inline-flex items-center gap-1 px-3 h-8 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50"
                >
                  <Plus size={12} /> Ajouter une tâche
                </button>
                <span className="text-[10px] text-zinc-500 font-mono">
                  Tap cellule = +2h · Double-tap = +8h · Long-press / right-click = effacer · Touche Entrée = nouvelle tâche
                </span>
              </div>
            </div>

            {/* Email block */}
            <div className="space-y-3" data-testid="wkcra-email-block">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Email</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input data-testid="wkcra-from-email" value={preview.from_email || ""} readOnly className="bg-zinc-50 text-xs font-mono" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input
                    data-testid="wkcra-to-email"
                    type="email"
                    list="wkcra-to-history"
                    value={toEmail}
                    onChange={(e) => { markDirty(); setToEmail(e.target.value); }}
                    placeholder="client@domain.com"
                    disabled={isLocked}
                  />
                  {toHistory.length > 0 && (
                    <datalist id="wkcra-to-history">
                      {toHistory.map((e) => <option key={e} value={e} />)}
                    </datalist>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">Cc <span className="text-zinc-400">(uniquement @ba-inmotion.com ou @{clientDomain || "domaine client"})</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="wkcra-cc-list">
                  {ccEmails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 text-[11px] rounded font-mono">
                      {e}
                      {!isLocked && <button onClick={() => removeCcEmail(e)} className="hover:text-red-700"><X size={10} /></button>}
                    </span>
                  ))}
                  {ccEmails.length === 0 && <span className="text-[11px] text-zinc-400 italic">Aucun Cc</span>}
                </div>
                {!isLocked && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      data-testid="wkcra-cc-input"
                      type="email"
                      list="wkcra-cc-history"
                      placeholder="add@example.com"
                      value={ccDraft}
                      onChange={(e) => setCcDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCcEmail(); } }}
                      className="flex-1 max-w-md text-xs"
                    />
                    {ccHistory.length > 0 && (
                      <datalist id="wkcra-cc-history">
                        {ccHistory.map((e) => <option key={e} value={e} />)}
                      </datalist>
                    )}
                    <button onClick={addCcEmail} data-testid="wkcra-cc-add" className="inline-flex items-center gap-1 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
                      <Plus size={12} /> Ajouter Cc
                    </button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Sujet</Label>
                <Input data-testid="wkcra-subject" value={subject} onChange={(e) => { markDirty(); setSubject(e.target.value); }} disabled={isLocked} />
              </div>
              <div>
                <Label className="text-xs">Corps du message</Label>
                <Textarea
                  data-testid="wkcra-body"
                  rows={7}
                  value={bodyText}
                  onChange={(e) => { markDirty(); setBodyText(e.target.value); }}
                  className="text-xs font-mono"
                  disabled={isLocked}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {!isLocked && !perDayCheck.ok && preview && (
            <div
              data-testid="wkcra-reconciliation-warning"
              className="w-full rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-[11px] flex flex-col gap-0.5"
            >
              <div className="font-semibold flex items-center gap-1.5">
                <span>⚠️</span>
                <span>Réconciliation incomplète — le bouton "Envoyer" est désactivé</span>
              </div>
              {perDayCheck.mismatches.map((m) => (
                <div key={m.dayIdx} className="font-mono">
                  · {m.label} : {m.taskSum}h sur tâches ≠ {m.timesheet}h timesheet
                </div>
              ))}
              <div className="mt-0.5 italic opacity-80">
                Chaque journée doit avoir ses heures entièrement réparties sur les tâches pour autoriser l'envoi.
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md"
            data-testid="wkcra-cancel"
          >
            {isLocked ? "Fermer" : "Annuler"}
          </button>
          <button
            type="button"
            onClick={onPreviewPdf}
            disabled={loading || !preview}
            className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-200 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-50"
            data-testid="wkcra-preview-pdf"
          >
            <Eye size={14} /> Aperçu PDF
          </button>
          {!isLocked && (
            <button
              type="button"
              onClick={onSend}
              disabled={sendDisabled}
              className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
              data-testid="wkcra-send"
              title={isFutureWeek ? "Vous ne pouvez pas envoyer un CRA pour une semaine future" : undefined}
            >
              <Send size={14} /> {sending ? "Envoi…" : "Envoyer le CRA hebdo"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* Iter-131: reason dialog when CEO/ADMIN sends on behalf ------------ */}
    <Dialog open={reasonDialogOpen} onOpenChange={(o) => { if (!o) { setReasonDialogOpen(false); setSubmissionReason(""); } }}>
      <DialogContent className="max-w-md" data-testid="wkcra-reason-dialog">
        <DialogHeader>
          <DialogTitle>Motif de soumission au nom de l'utilisateur</DialogTitle>
          <DialogDescription className="text-xs">
            Un motif est obligatoire — il sera notifié à <strong>{impersonatedUserName || "l'utilisateur"}</strong> par email et notification in-app.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <label className="text-xs text-zinc-700 font-medium">Motif</label>
          <textarea
            data-testid="wkcra-reason-input"
            autoFocus
            value={submissionReason}
            onChange={(e) => setSubmissionReason(e.target.value)}
            rows={4}
            placeholder="Ex. Le collaborateur est en arrêt maladie. Soumission au nom du collaborateur pour respecter le délai client."
            className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <DialogFooter>
          <button onClick={() => { setReasonDialogOpen(false); setSubmissionReason(""); }} className="px-4 h-10 text-sm border border-zinc-300 rounded-md hover:bg-zinc-50">
            Annuler
          </button>
          <button
            data-testid="wkcra-reason-submit"
            onClick={onSubmitReason}
            disabled={sending || !(submissionReason || "").trim()}
            className="inline-flex items-center gap-2 px-4 h-10 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 disabled:opacity-50"
          >
            <Send size={14} /> {sending ? "Envoi…" : "Confirmer et envoyer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
